import { type ReactNode, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { ProgramAchievementGraphic } from 'ShareableGraphics/components/ProgramAchievementGraphic/ProgramAchievementGraphic';
import {
    type ProgramAchievementGraphicKey,
    type ProgramAchievementGraphicUser,
} from 'ShareableGraphics/ShareableGraphics.types';
import { getCohort, getGraduationStatus } from 'Users';
import { getBranding, getIsExecEd } from 'Cohorts';
import { Modal } from 'Modal';
import { InlineLink } from 'InlineLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { MessageBubbleWithLogo } from '../MessageBubbleWithLogo';

function ProgramAchievementMessageComponent({
    children,
    variant,
}: {
    children: ReactNode;
    variant: ProgramAchievementGraphicKey;
}) {
    const [modalOpen, setModalOpen] = useState(false);
    const { appMenuProgramLogo, branding } = useTargetBrandConfig();
    const currentUser = useCurrentUser();
    const handleCustomizeAndDownloadPress = useCallback(() => {
        setModalOpen(true);
    }, []);
    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    if (!currentUser) return null;

    const cohort = getCohort(currentUser);
    if (!cohort) return null;

    const graphicUser: ProgramAchievementGraphicUser = {
        name: currentUser.name,
        graduationStatus: getGraduationStatus(currentUser),
        cohort: {
            branding: getBranding(cohort),
            // Cohort.graduationDate is a Unix timestamp in seconds; the graphic renders it via moment() (ms).
            graduationDate: cohort.graduationDate * 1000,
            isExecEd: getIsExecEd(cohort),
            programType: cohort.programType,
            startDate: cohort.startDate,
            title: cohort.title,
        },
    };
    return (
        <ProgramAchievementMessagePresentation
            logoSrc={appMenuProgramLogo}
            altText={branding}
            onCustomizeAndDownloadPress={handleCustomizeAndDownloadPress}
            currentUser={graphicUser}
            variant={variant}
            modalOpen={modalOpen}
            handleCloseModal={handleCloseModal}
        >
            {children}
        </ProgramAchievementMessagePresentation>
    );
}

function ProgramAchievementMessagePresentation({
    logoSrc,
    altText,
    onCustomizeAndDownloadPress,
    currentUser,
    variant,
    children,
    modalOpen,
    handleCloseModal,
}: {
    logoSrc: string | null;
    altText: string;
    onCustomizeAndDownloadPress: () => void;
    currentUser: ProgramAchievementGraphicUser;
    variant: ProgramAchievementGraphicKey;
    children: ReactNode;
    modalOpen: boolean;
    handleCloseModal: () => void;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'programAchievementMessage' });
    const buttonText = t('customizeAndDownload');
    const buttonWords = buttonText.split(' ');
    const lastWord = buttonWords.length > 1 ? buttonWords.pop() : buttonText;
    const firstWords = buttonWords.length > 1 ? buttonWords.join(' ') : '';

    return (
        <>
            <MessageBubbleWithLogo logoSrc={logoSrc} altText={altText} textBubbleShown dismissable={false}>
                <div className="flex items-start justify-between gap-5">
                    <div>
                        <div className="mb-0 xs:mb-3.5">{children}</div>
                        <InlineLink className="text-start font-semibold" onPress={onCustomizeAndDownloadPress}>
                            {firstWords}{' '}
                            <span className="whitespace-nowrap">
                                {lastWord}{' '}
                                <FontAwesomeIcon className="ms-1 inline-block rtl:rotate-180" icon={faArrowRight} />
                            </span>
                        </InlineLink>
                    </div>
                    <div className="flex shrink-0 items-center justify-center self-center">
                        <button
                            type="button"
                            onClick={onCustomizeAndDownloadPress}
                            aria-label={t('customizeAndDownload')}
                            className="relative flex h-[80px] w-[61px] items-center justify-center overflow-clip rounded-5 border-none bg-transparent p-0"
                        >
                            <ProgramAchievementGraphic
                                thumbnailOnly
                                variant={variant}
                                collapsed={false}
                                currentUser={currentUser}
                                xsOrSm={false}
                            />
                        </button>
                    </div>
                </div>
            </MessageBubbleWithLogo>
            {modalOpen && (
                <Modal onClose={handleCloseModal} size="unset" transparent>
                    <ProgramAchievementGraphic
                        className="h-auto w-[75vw] max-w-[350px]"
                        variant={variant}
                        collapsed={false}
                        currentUser={currentUser}
                        xsOrSm={false}
                    />
                </Modal>
            )}
        </>
    );
}

export const ProgramAchievementMessage = memo(
    ProgramAchievementMessageComponent,
) as typeof ProgramAchievementMessageComponent;
